import {
  AnswerParts,
  ApiDateTime,
  BulkQuestionUpdate,
  CoreResponseId,
  CreateAutoComplete,
  CreateQuestionDocumentation,
  CreateReviewAction,
  DocumentationId,
  LayerType,
  OwnerId,
  PromptModifiers,
  QuestionId,
  QuestionStatus,
  ResponseOption,
  ScopeId,
} from "platformed-client/types";
import {invalidateQueries, q} from "../../state";
import {platformedClient} from "..";

export async function get(questionId: QuestionId) {
  return await platformedClient.internalRequest("get", `/region/{region}/api/vendor_toolkit/questions/{question_id}`, {
    path: {question_id: questionId},
  });
}

export async function load(questionIds: QuestionId[]) {
  return await platformedClient.internalRequest("post", `/region/{region}/api/vendor_toolkit/questions/load`, {
    body: questionIds,
  });
}

export async function assign(questionId: QuestionId, layerType: LayerType, ownerId: OwnerId | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/assign`,
    {body: ownerId, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.watchingQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.tasks.filter(),
  ]);
  return res;
}

export async function updateStatus(questionId: QuestionId, layerType: LayerType, status: QuestionStatus) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/status`,
    {body: status, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.tasks.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
}

export async function updateDueDate(questionId: QuestionId, layerType: LayerType, dueDate: ApiDateTime | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/due_date`,
    {body: dueDate, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.tasks.filter(),
  ]);
  return res;
}

export async function updateNumber(questionId: QuestionId, questionNumber: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/number`,
    {body: questionNumber, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
}

export async function updateText(questionId: QuestionId, text: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/text`,
    {body: text, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
}

export async function updateGuidance(questionId: QuestionId, guidance: string) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/guidance`,
    {body: guidance, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
  ]);
  return res;
}

export async function updateResponseText(questionId: QuestionId, layerType: LayerType, responseText: string | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/response_text`,
    {body: responseText, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
  ]);
  return res;
}

export async function updateResponseYesNo(
  questionId: QuestionId,
  layerType: LayerType,
  responseYesNo: boolean | undefined,
) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/response_yes_no`,
    {body: responseYesNo ?? null, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
  ]);
  return res;
}

export async function updateResponseSelect(
  questionId: QuestionId,
  layerType: LayerType,
  responseSelect: ResponseOption[] | undefined,
) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/response_select`,
    {body: responseSelect ?? null, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
  ]);
  return res;
}

export async function updateLinkedCoreResponse(
  questionId: QuestionId,
  layerType: LayerType,
  coreResponse: CoreResponseId | null,
) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/core_response`,
    {body: coreResponse, path: {question_id: questionId, layer_type: layerType}},
  );

  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
  ]);
  return res;
}

export async function updateParts(questionId: QuestionId, parts: AnswerParts) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/parts`,
    {body: parts, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
  ]);
  return res;
}

export async function updatePromptModifiers(questionId: QuestionId, promptModifiers: PromptModifiers) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/prompt_modifiers`,
    {body: promptModifiers, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
  ]);
  return res;
}

export async function updateOverrideLimitedToScopes(questionId: QuestionId, overrideEnabled: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/override_limited_to_scopes`,
    {body: overrideEnabled, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
}

export async function updateLimitedToScopes(questionId: QuestionId, scopeIds: ScopeId[] | null) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/limited_to_scopes`,
    {body: scopeIds, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
}

export const createDocumentation = async (
  questionId: QuestionId,
  layerType: LayerType,
  documentation: CreateQuestionDocumentation[],
) => {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/documentation`,
    {body: documentation, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionDocumentation.filter(questionId),
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaireDocumentation.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
};

export const listDocumentation = async (questionId: QuestionId, layerType: LayerType) => {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/documentation`,
    {path: {question_id: questionId, layer_type: layerType}},
  );
};

export const deleteDocumentation = async (
  questionId: QuestionId,
  layerType: LayerType,
  documentationId: DocumentationId,
) => {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/documentation/{documentation_id}`,
    {path: {question_id: questionId, layer_type: layerType, documentation_id: documentationId}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionDocumentation.filter(questionId),
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaireDocumentation.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
};

export const deleteAllDocumentation = async (questionId: QuestionId, layerType: LayerType) => {
  const res = await platformedClient.internalRequest(
    "delete",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/documentation`,
    {path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionDocumentation.filter(questionId),
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaireDocumentation.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
};

export async function bulkUpdate(update: BulkQuestionUpdate) {
  const res = await platformedClient.internalRequest("patch", `/region/{region}/api/vendor_toolkit/questions`, {
    body: update,
  });
  await invalidateQueries([
    q.vendorToolkit.questionnaireDocumentation.filter(),
    q.vendorToolkit.question.filter(),
    q.vendorToolkit.facts.filter(),
    q.vendorToolkit.factCounts.filter(),
    q.vendorToolkit.tasks.filter(),
    q.vendorToolkit.adhocQuestion.filter(),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.questionnaire.filter(),
  ]);
  return res;
}

export async function automaticallyAnswer(questionId: QuestionId, payload: CreateAutoComplete) {
  const res = platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/auto_complete`,
    {body: payload, path: {question_id: questionId}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.questionnaireAutoComplete.filter(),
  ]);
  return res;
}

export async function listUsedFacts(questionId: QuestionId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/used_facts`,
    {path: {question_id: questionId}},
  );
}

export async function listGeneratedFacts(questionId: QuestionId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/generated_facts`,
    {path: {question_id: questionId}},
  );
}

export async function copyBack(questionId: QuestionId, layerType: LayerType) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/copy_back`,
    {path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.questionnaireDocumentation.filter(),
    q.vendorToolkit.questionnaire.filter(),
    q.vendorToolkit.adhocQuestions.filter(),
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.vendorToolkit.questionDocumentation.filter(questionId),
  ]);
  return res;
}

export async function getWatching(questionId: QuestionId) {
  return await platformedClient.internalRequest(
    "get",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/watching`,
    {path: {question_id: questionId}},
  );
}

export async function setWatching(questionId: QuestionId, isWatching: boolean) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/watching`,
    {body: isWatching, path: {question_id: questionId}},
  );
  await invalidateQueries([q.vendorToolkit.watchingQuestion.filter(questionId)]);
  return res;
}

export async function createReviews(questionId: QuestionId, layerType: LayerType, requests: CreateReviewAction[]) {
  const res = await platformedClient.internalRequest(
    "post",
    `/region/{region}/api/vendor_toolkit/questions/{question_id}/response_layers/{layer_type}/reviews`,
    {body: requests, path: {question_id: questionId, layer_type: layerType}},
  );
  await invalidateQueries([
    q.vendorToolkit.question.filter(questionId),
    q.vendorToolkit.adhocQuestion.filter(questionId),
    q.reviewable.filter(),
  ]);
  return res;
}
